import {useMemo} from 'react';
import {ColumnDef} from '@tanstack/react-table';
import {useIntl} from 'react-intl';
import TableMetronic from '../../../app/common/components/table/TableMetronic';
import {EtatLien, ListService} from '../Types/service';
import {getStatusColor, getStatusLabel} from '../Helpers/stateHelper';
import {
  accessorService,
  accessorSite,
  getBuColLabel,
  getServiceColLabel,
  getSiteColLabel,
} from '../Helpers/ServiceHelper';
import EtatBadge from './EtatBadge';
import ConnexionDot from './ConnexionDot';

interface ServiceChildrenTableProps {
  services: ListService[];
}

const ServiceChildrenTable = ({services}: ServiceChildrenTableProps): JSX.Element => {
  const intl = useIntl();

  const columns = useMemo<ColumnDef<ListService>[]>(
    () => [
      {
        header: intl.formatMessage({id: 'SERVICES.COL.SERVICE'}),
        accessorKey: 'service',
        cell: getServiceColLabel,
        accessorFn: accessorService,
      },
      {
        header: intl.formatMessage({id: 'SERVICES.COL.ACCOUNT'}),
        accessorKey: 'buId',
        cell: getBuColLabel,
        size: 150,
      },
      {
        header: intl.formatMessage({id: 'SERVICES.COL.SITE'}),
        accessorKey: 'siteId',
        cell: getSiteColLabel,
        accessorFn: accessorSite,
        size: 200,
      },
      {
        header: intl.formatMessage({id: 'SERVICES.COL.TYPE'}),
        accessorKey: 'type',
        size: 50,
        accessorFn: (row) => (row.is4g ? '4G' : intl.formatMessage({id: 'SERVICES.TYPE.WIRED'})),
        cell: (info) =>
          info.row.original.is4g ? '4G' : intl.formatMessage({id: 'SERVICES.TYPE.WIRED'}),
      },
      {
        header: intl.formatMessage({id: 'SERVICES.COL.STATUS'}),
        accessorKey: 'state',
        size: 90,
        cell: (info) => {
          const status = info.row.original.state;
          const color = getStatusColor(status);
          return status ? <EtatBadge color={color} label={getStatusLabel(status, intl)} /> : '-';
        },
      },
      {
        header: intl.formatMessage({id: 'SERVICES.COL.STATE'}),
        accessorKey: 'connexion',
        size: 80,
        accessorFn: (row) => row.monitoring?.etat ?? '',
        cell: (info) => {
          const monitoring = info.row.original.monitoring;
          const etat = monitoring?.etat as EtatLien | undefined;
          if (!etat) return '-';
          if (etat === 'CONNECTED')
            return (
              <ConnexionDot
                color="#50cd89"
                label={intl.formatMessage({id: 'LIEN.DETAIL.STATE.CONNECTED'})}
              />
            );
          if (etat === 'DISCONNECTED')
            return (
              <ConnexionDot
                color="#f1416c"
                label={intl.formatMessage({id: 'LIEN.DETAIL.STATE.DISCONNECTED'})}
              />
            );
          return (
            <ConnexionDot
              color="#a1a5b7"
              label={intl.formatMessage({id: 'SERVICES.CONNEXION.TO_CONFIGURE'})}
            />
          );
        },
      },
    ],
    [intl]
  );

  return (
    <div className="px-10 py-4">
      <TableMetronic<ListService>
        data={services}
        columns={columns}
        showCardHeader={false}
        searchBar={false}
        pagination={false}
        getRowId={(row) => String(row.serviceId)}
        noRowsMessage={intl.formatMessage({id: 'TABLE.NO_ROWS'})}
      />
    </div>
  );
};

export default ServiceChildrenTable;
